import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ApiError } from '../../services/apiClient';
import spottoLogo from '../../assets/spotto-logo.png';
import { TermsModal } from '../common/TermsModal';

const APP_STORE_URL = 'https://apps.apple.com/app/spotto/id6740039498';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=pl.spotto.mobile';

export const BusinessRegister: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signUp, isLoading, isAuthenticated } = useAuth();

  // Form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phone, setPhone] = useState('');

  // Terms checkboxes
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [agreeToPrivacy, setAgreeToPrivacy] = useState(false);

  // Modals
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // UI state
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [requiresVerification, setRequiresVerification] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  // Redirect to home if already authenticated and not showing verification message
  useEffect(() => {
    if (isAuthenticated && !requiresVerification) {
      navigate('/setup/business-profile', { replace: true });
    }
  }, [isAuthenticated, navigate, requiresVerification]);

  const validateForm = (): boolean => {
    if (!firstName.trim()) {
      setError(t('businessRegister.validation.firstNameRequired'));
      return false;
    }
    if (!lastName.trim()) {
      setError(t('businessRegister.validation.lastNameRequired'));
      return false;
    }
    if (!email.trim()) {
      setError(t('businessRegister.validation.emailRequired'));
      return false;
    }
    if (!email.includes('@')) {
      setError(t('businessRegister.validation.emailInvalid'));
      return false;
    }
    if (!password) {
      setError(t('businessRegister.validation.passwordRequired'));
      return false;
    }
    if (password.length < 6) {
      setError(t('businessRegister.validation.passwordTooShort'));
      return false;
    }
    if (password !== confirmPassword) {
      setError(t('businessRegister.validation.passwordsDoNotMatch'));
      return false;
    }
    if (!agreeToTerms) {
      setError(t('businessRegister.validation.termsRequired'));
      return false;
    }
    if (!agreeToPrivacy) {
      setError(t('businessRegister.validation.privacyRequired'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    try {
      const response = await signUp({
        email: email.trim(),
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim() || undefined,
        roles: ['Player', 'Business'],
        playerTerms: true,
        businessTerms: true,
        trainerTerms: false,
      });

      if (response.requiresEmailVerification) {
        setRequiresVerification(true);
        setRegisteredEmail(email);
      } else {
        // Auto-login was handled by AuthContext, redirect to business profile setup
        navigate('/setup/business-profile', { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.message?.toLowerCase().includes('email') || err.message?.toLowerCase().includes('exists')) {
          setError(t('businessRegister.errors.emailExists'));
        } else {
          setError(err.message || t('businessRegister.errors.registrationFailed'));
        }
      } else {
        setError(t('businessRegister.errors.registrationFailed'));
      }
    }
  };

  // Email verification success screen
  if (requiresVerification) {
    return (
      <div className="min-h-screen bg-[#23262b] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-[#2c3036] rounded-2xl border border-white/[0.08] p-8 shadow-xl text-center">
            {/* Success Icon */}
            <div className="mx-auto w-16 h-16 bg-[#4d63ac]/15 rounded-full flex items-center justify-center mb-6">
              <svg className="w-8 h-8 text-[#7d8dcd]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>

            <h2 className="text-2xl font-bold text-white mb-2">{t('emailVerification.title')}</h2>
            <p className="text-[#9aa3b2] mb-4">{t('emailVerification.subtitle')}</p>
            <p className="text-sm text-[#9aa3b2] mb-6">
              <span className="text-[#7d8dcd]">{registeredEmail}</span>
            </p>
            <p className="text-sm text-[#9aa3b2] mb-6">{t('emailVerification.checkInbox')}</p>

            <Link
              to="/login"
              className="inline-block w-full py-3 px-4 bg-[#4d63ac] hover:bg-[#5b71be] text-white font-semibold rounded-lg transition-colors"
            >
              {t('businessRegister.signIn')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Promotional Content */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          backgroundImage: `url('/bg.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#23262b]/88 via-[#35435d]/60 to-[#4d63ac]/35"></div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          {/* Top - Logo wordmark */}
          <div className="flex items-center gap-3.5">
            <img
              src={spottoLogo}
              alt="Spotto"
              className="h-14 w-auto"
              style={{
                filter:
                  "drop-shadow(0 6px 14px rgba(0, 0, 0, 0.45)) drop-shadow(0 0 28px rgba(77, 99, 172, 0.50))",
              }}
            />
            <span
              className="font-display text-3xl font-semibold tracking-tight text-white"
              style={{
                textShadow: "0 2px 10px rgba(0, 0, 0, 0.45)",
                letterSpacing: "-0.02em",
              }}
            >
              Spotto
            </span>
          </div>

          {/* Middle - Main Content */}
          <div className="space-y-8">
            <div>
              <h1 className="text-5xl font-bold mb-4">{t('login.heroTitle')}</h1>
              <p className="text-xl text-[#c5cde0] leading-relaxed max-w-lg">
                {t('login.heroDescription')}
              </p>
            </div>

            {/* Features */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/10 border border-white/15 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className="text-[#c5cde0]">{t('login.feature1')}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/10 border border-white/15 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="text-[#c5cde0]">{t('login.feature2')}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/10 border border-white/15 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <span className="text-[#c5cde0]">{t('login.feature3')}</span>
              </div>
            </div>

            {/* Download App Section */}
            <div className="pt-4">
              <p className="text-sm text-[#9aa3b2] mb-4">{t('login.downloadApp')}</p>
              <div className="flex gap-3">
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg transition-colors border border-white/20"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <span className="text-sm font-medium">App Store</span>
                </a>
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg transition-colors border border-white/20"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  <span className="text-sm font-medium">Google Play</span>
                </a>
              </div>
            </div>
          </div>

          {/* Bottom - Footer */}
          <div className="text-sm text-[#9aa3b2]">
            © {new Date().getFullYear()} Spotto. {t('login.allRightsReserved')}
          </div>
        </div>
      </div>

      {/* Right Side - Registration Form */}
      <div className="w-full lg:w-1/2 bg-[#23262b] flex items-center justify-center p-4 overflow-y-auto">
        <div className="w-full max-w-md py-8">
          {/* Mobile Logo */}
          <div className="text-center mb-6 lg:hidden">
            <div className="inline-flex items-center justify-center mb-4">
              <img
                src={spottoLogo}
                alt="Spotto Logo"
                className="h-16 w-auto"
              />
            </div>
          </div>

          {/* Registration Form */}
          <div className="bg-[#2c3036] rounded-2xl border border-white/[0.08] p-8 shadow-xl">
            <h2 className="text-2xl font-bold text-white mb-2">{t('businessRegister.title')}</h2>
            <p className="text-[#9aa3b2] mb-6">{t('businessRegister.subtitle')}</p>

            {error && (
              <div className="mb-6 p-4 bg-[#c06f57]/10 border border-[#c06f57]/30 rounded-lg">
                <p className="text-sm text-[#e1a08a]">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name Fields - Two columns */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-[#c5cde0] mb-2">
                    {t('businessRegister.firstName')} *
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent transition-all"
                    disabled={isLoading}
                    autoComplete="given-name"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-[#c5cde0] mb-2">
                    {t('businessRegister.lastName')} *
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent transition-all"
                    disabled={isLoading}
                    autoComplete="family-name"
                  />
                </div>
              </div>

              {/* Email Input */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#c5cde0] mb-2">
                  {t('businessRegister.email')} *
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent transition-all"
                  placeholder={t('auth.emailPlaceholder')}
                  disabled={isLoading}
                  autoComplete="email"
                />
              </div>

              {/* Phone Input */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-[#c5cde0] mb-2">
                  {t('businessRegister.phoneOptional')}
                </label>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent transition-all"
                  placeholder="+48 123 456 789"
                  disabled={isLoading}
                  autoComplete="tel"
                />
              </div>

              {/* Password Input */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-[#c5cde0] mb-2">
                  {t('businessRegister.password')} *
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent transition-all"
                    placeholder={t('auth.passwordPlaceholder')}
                    disabled={isLoading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9aa3b2] hover:text-[#c5cde0] transition-colors"
                    disabled={isLoading}
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Confirm Password Input */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-[#c5cde0] mb-2">
                  {t('businessRegister.confirmPassword')} *
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent transition-all"
                    placeholder={t('auth.passwordPlaceholder')}
                    disabled={isLoading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9aa3b2] hover:text-[#c5cde0] transition-colors"
                    disabled={isLoading}
                  >
                    {showConfirmPassword ? (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Terms Checkboxes */}
              <div className="space-y-3 pt-2">
                {/* Terms of Service */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    className="mt-1 w-5 h-5 rounded border-white/[0.15] bg-[#2a2e34] text-[#4d63ac] focus:ring-[#4d63ac] focus:ring-offset-[#2c3036]"
                    disabled={isLoading}
                  />
                  <span className="text-sm text-[#c5cde0]">
                    {t('businessRegister.acceptTerms')}{' '}
                    <button
                      type="button"
                      onClick={() => setShowTermsModal(true)}
                      className="text-[#7d8dcd] hover:text-[#9aa8d9] underline"
                    >
                      {t('businessRegister.termsOfService')}
                    </button>
                    {' *'}
                  </span>
                </label>

                {/* Privacy Policy */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={agreeToPrivacy}
                    onChange={(e) => setAgreeToPrivacy(e.target.checked)}
                    className="mt-1 w-5 h-5 rounded border-white/[0.15] bg-[#2a2e34] text-[#4d63ac] focus:ring-[#4d63ac] focus:ring-offset-[#2c3036]"
                    disabled={isLoading}
                  />
                  <span className="text-sm text-[#c5cde0]">
                    {t('businessRegister.acceptPrivacy')}{' '}
                    <button
                      type="button"
                      onClick={() => setShowPrivacyModal(true)}
                      className="text-[#7d8dcd] hover:text-[#9aa8d9] underline"
                    >
                      {t('businessRegister.privacyPolicy')}
                    </button>
                    {' *'}
                  </span>
                </label>
              </div>

              {/* Create Account Button */}
              <button
                type="submit"
                disabled={isLoading || !agreeToTerms || !agreeToPrivacy}
                className="w-full py-3 px-4 bg-[#4d63ac] hover:bg-[#5b71be] disabled:bg-[#4d63ac]/50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:ring-offset-2 focus:ring-offset-[#2c3036] mt-6"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('businessRegister.creatingAccount')}
                  </span>
                ) : (
                  t('businessRegister.createAccount')
                )}
              </button>
            </form>
          </div>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-sm text-[#9aa3b2]">
              {t('businessRegister.alreadyHaveAccount')}{' '}
              <Link to="/login" className="text-[#4d63ac] hover:text-[#7d8dcd] font-medium transition-colors">
                {t('businessRegister.signIn')}
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Terms Modal */}
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        type="terms"
      />

      {/* Privacy Modal */}
      <TermsModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        type="privacy"
      />
    </div>
  );
};
